import { Box, Button, Grid, Typography, TextField, IconButton, Checkbox } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Quote, QuoteLineItem } from '../types/api';
import { useQuoteLineItems } from '../hooks/useQuoteLineItems';
import { createQuote, updateQuote } from '../services/quotes.service';
import { QuotesRoute, ViewQuoteRoute } from '../routes';
import GlobalMessageContext from '../context/globalMessage.context';
import { QuoteSummary } from '../components/quote-summary.component';
import { CreateQuoteLineItemModal } from '../components/modal.create-quote-lineitem';
import { dataTables } from '../styles';


type QuoteFormState = Omit<Partial<Quote>, 'issueDate' | 'expiryDate'> & {
  issueDate?: Date | null;
  expiryDate?: Date | null;
};

export default function EditQuoteView() {
  const uxMessage = useContext(GlobalMessageContext);
  const navigate = useNavigate();
  const { state } = useLocation();

  const mode = state?.mode || 'create';
  const existingQuote = state?.quote;
  const title = mode === 'create' ? 'Create Quote' : `Edit Quote ${existingQuote?.number || ''}`;

  // Quote state. Dates are held as Date objects while editing (the API sends
  // strings; they're converted on load and back with toISOString on save).
  const [quote, setQuote] = useState<QuoteFormState>({
    customerName: existingQuote?.customerName || '',
    customerEmail: existingQuote?.customerEmail || '',
    customerPhone: existingQuote?.customerPhone || '',
    issueDate: existingQuote?.issueDate ? new Date(existingQuote.issueDate) : new Date(),
    expiryDate: existingQuote?.expiryDate ? new Date(existingQuote.expiryDate) : null,
    status: existingQuote?.status || 'DRAFT',
    lineItems: existingQuote?.lineItems || [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);

  // Initialize line items hook
  const {
    items: lineItems,
    summary,
    addItem,
    removeItem,
    updateQuantity,
    updateField,
    getCleanItems,
  } = useQuoteLineItems(quote.lineItems || []);

  // Update quote totals when line items change
  useEffect(() => {
    setQuote((prev) => ({
      ...prev,
      subTotal: summary.subTotal,
      taxAmount: summary.taxAmount,
      total: summary.total,
    }));
  }, [summary]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!quote.customerName || quote.customerName.trim() === '') {
      newErrors.customerName = 'Customer name is required';
    }

    if (summary.itemCount === 0) {
      newErrors.lineItems = 'Please add at least one line item';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      uxMessage.setMessage({
        message: 'Please fix the validation errors',
        severity: 'warning',
      });
      return;
    }

    const cleanLineItems = getCleanItems();

    // Prepare DTO with only the fields the backend expects
    const quoteData: any = {
      customerName: quote.customerName,
      issueDate: quote.issueDate?.toISOString(),
      lineItems: cleanLineItems.map((item: any) => ({
        description: item.description,
        sku: item.sku,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        taxRate: item.taxRate,
        variantGuid: item.variantGuid,
        sequence: item.sequence,
      })),
    };

    // Only include optional fields if they have values
    if (quote.customerEmail && quote.customerEmail.trim() !== '') {
      quoteData.customerEmail = quote.customerEmail;
    }
    if (quote.customerPhone && quote.customerPhone.trim() !== '') {
      quoteData.customerPhone = quote.customerPhone;
    }
    if (quote.expiryDate) {
      quoteData.expiryDate = quote.expiryDate.toISOString();
    }

    try {
      if (mode === 'edit' && existingQuote?.guid) {
        const updated = await updateQuote(existingQuote.guid, quoteData);
        uxMessage.setMessage({
          message: `Quote ${updated.number} successfully updated`,
          severity: 'success',
        });
        navigate(ViewQuoteRoute, { state: { quote: updated } });
      } else {
        const created = await createQuote(quoteData);
        uxMessage.setMessage({
          message: `Quote ${created.number} successfully created`,
          severity: 'success',
        });
        navigate(ViewQuoteRoute, { state: { quote: created } });
      }
    } catch (error: any) {
      console.error('Error saving quote:', error);
      uxMessage.setMessage({
        message: `Error: ${error.response?.data?.message || error.message || 'Failed to save quote'}`,
        severity: 'error',
      });
    }
  };

  const handleCancel = () => {
    navigate(QuotesRoute);
  };

  const handleAddCustomLineItem = () => {
    setEditingItem(null);
    setShowAddItemModal(true);
  };

  const handleEditLineItem = (item: any) => {
    setEditingItem(item);
    setShowAddItemModal(true);
  };

  const handleSaveCustomLineItem = (item: Partial<QuoteLineItem>) => {
    if (editingItem) {
      // Update existing item
      updateField(editingItem.tempId, 'description', item.description);
      updateField(editingItem.tempId, 'sku', item.sku);
      updateField(editingItem.tempId, 'unitPrice', item.unitPrice);
      updateField(editingItem.tempId, 'taxRate', item.taxRate);
      updateQuantity(editingItem.tempId, item.quantity || 1);
    } else {
      // Add new item
      addItem(item);
    }
    setShowAddItemModal(false);
    setEditingItem(null);
  };

  const handleAddProduct = () => {
    // This will open a product search modal in the future
    uxMessage.setMessage({
      message: 'Product search will be implemented with Xero integration (Task 4)',
      severity: 'info',
    });
  };

  const handleDeleteLineItem = (tempId: string) => {
    if (summary.itemCount === 1) {
      uxMessage.setMessage({
        message: 'Cannot delete the last item',
        severity: 'error',
      });
      return;
    }
    removeItem(tempId);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(value / 100);
  };

  // Define DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'sku',
      headerName: 'SKU',
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      minWidth: 200,
      flex: 2,
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      minWidth: 100,
      flex: 1,
      align: 'right',
      renderCell: (params) => (
        <TextField
          size="small"
          type="number"
          value={params.row.quantity}
          onChange={(e) => updateQuantity(params.row.tempId, parseInt(e.target.value) || 1)}
          inputProps={{ min: 1, style: { textAlign: 'right' } }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
          }}
        />
      ),
    },
    {
      field: 'unitPrice',
      headerName: 'Unit Price',
      minWidth: 120,
      flex: 1,
      align: 'right',
      renderCell: (params) => formatCurrency(params.row.unitPrice),
    },
    {
      field: 'taxable',
      headerName: 'Taxable?',
      minWidth: 100,
      flex: 1,
      align: 'center',
      renderCell: (params) => (
        <Checkbox
          checked={params.row.taxRate > 0}
          disabled
          size="small"
        />
      ),
    },
    {
      field: 'lineTotal',
      headerName: 'Line Total',
      minWidth: 120,
      flex: 1,
      align: 'right',
      renderCell: (params) => formatCurrency(params.row.lineTotal),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      flex: 1,
      align: 'center',
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isCustomItem = !params.row.sku || params.row.sku === '';
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleEditLineItem(params.row)}
              disabled={!isCustomItem}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteLineItem(params.row.tempId)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Typography variant="h1">{title}</Typography>

      <Box sx={{ m: 1 }}>
        <Grid container spacing={2}>
          {/* Customer Details & Date */}
          <Grid size={8}>
            <Grid container spacing={2}>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="Customer Name"
                  value={quote.customerName}
                  onChange={(e) =>
                    setQuote({ ...quote, customerName: e.target.value })
                  }
                  error={!!errors.customerName}
                  helperText={errors.customerName}
                  required
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={quote.customerEmail || ''}
                  onChange={(e) =>
                    setQuote({ ...quote, customerEmail: e.target.value })
                  }
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={quote.customerPhone || ''}
                  onChange={(e) =>
                    setQuote({ ...quote, customerPhone: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={4}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Issue Date"
                    value={quote.issueDate}
                    onChange={(date) => setQuote({ ...quote, issueDate: date })}
                    format="dd-MM-yyyy"
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Expiry Date"
                    value={quote.expiryDate}
                    onChange={(date) => setQuote({ ...quote, expiryDate: date })}
                    format="dd-MM-yyyy"
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Grid>

          {/* Summary Table */}
          {summary.itemCount > 0 && (
            <Grid size={4}>
              <QuoteSummary
                quote={quote}
                subTotal={summary.subTotal}
                taxAmount={summary.taxAmount}
                total={summary.total}
                hideDate
              />
            </Grid>
          )}

          {/* Line Items */}
          <Grid size={12}>
            <Typography variant="h2">Line Items</Typography>

            {errors.lineItems && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {errors.lineItems}
              </Typography>
            )}

            {lineItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                No line items added yet. Click "Add Product" or "Add Custom Item" to get started.
              </Typography>
            ) : (
              <Box sx={{ my: 2 }}>
                <DataGrid
                  rows={lineItems}
                  columns={columns}
                  getRowId={(row) => row.tempId}
                  autoHeight
                  density="compact"
                  rowHeight={60}
                  disableRowSelectionOnClick
                  hideFooter
                  sx={dataTables}
                />
              </Box>
            )}

            <Button
              variant="contained"
              color="secondary"
              onClick={handleAddProduct}
              sx={{ margin: 1 }}
            >
              Add Product
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleAddCustomLineItem}
              sx={{ margin: 1 }}
            >
              Add Custom Item
            </Button>
          </Grid>

          {/* Actions */}
          <Grid size={12}>
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                p: 2,
                mt: 3,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Button variant="contained" color="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave}>
                {mode === 'create' ? 'Save as Draft' : 'Update'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Add Custom Line Item Modal */}
      <CreateQuoteLineItemModal
        open={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        onSave={handleSaveCustomLineItem}
        skuRequired={false}
        editingItem={editingItem}
      />
    </>
  );
}
